import { useRef, useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { Upload, Loader2, Check, AlertTriangle, Edit2 } from "lucide-react";
import { apiClient } from "@/App";
import { toast } from "sonner";
import { tokens } from "@/design-system/tokens";

export const IcsImport = ({ onComplete }) => {
  // step: idle | loading | preview | done
  const [step, setStep] = useState("idle");
  const fileInputRef = useRef(null);

  const [events, setEvents] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [totalEvents, setTotalEvents] = useState(0);
  const [importing, setImporting] = useState(false);
  const [editIdx, setEditIdx] = useState(null);

  // ── Step 1: upload + parse ────────────────────────────────────────────────
  const handleFile = async (file) => {
    if (!file) return;
    setStep("loading");
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await apiClient.post("/calendar/import/ics-preview", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const gigs = (res.data.potential_gigs || []).map(g => ({ ...g, is_public: true }));
      setEvents(gigs);
      setTotalEvents(res.data.total_events || 0);
      const ids = new Set();
      gigs.forEach((g, i) => { if (!g.is_duplicate) ids.add(i); });
      setSelectedIds(ids);
      setStep("preview");
      if (gigs.length === 0) toast.info("No potential gigs found in this calendar file.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to parse ICS file");
      setStep("idle");
    }
  };

  // ── Preview helpers ───────────────────────────────────────────────────────
  const toggleSelect = (idx) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(events.map((_, i) => i)));
  const deselectAll = () => setSelectedIds(new Set());

  const updateEvent = (idx, field, value) => {
    setEvents(prev => prev.map((e, i) => i === idx ? { ...e, [field]: value } : e));
  };

  // ── Step 2: confirm import ────────────────────────────────────────────────
  const handleImport = async () => {
    const toImport = events.filter((_, i) => selectedIds.has(i));
    if (toImport.length === 0) { toast.error("Select at least one event to import"); return; }
    setImporting(true);
    try {
      const res = await apiClient.post("/calendar/import/confirm", { gigs: toImport });
      toast.success(`Imported ${res.data.imported} gig(s)!`);
      setStep("done");
      if (onComplete) onComplete(res.data.imported);
    } catch {
      toast.error("Failed to import gigs");
    } finally {
      setImporting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (step === "idle") {
    // No heading of its own — this renders inside Settings' "ICS / iCal file"
    // IntegrationRow, whose title/description used to be near-duplicated here
    // (same fix as CalendarImport's idle step). Just the upload button,
    // right-aligned to match the other rows' action placement.
    return (
      <div style={{ display: "flex", justifyContent: "flex-end" }} data-testid="ics-import-idle">
        <button
          onClick={() => fileInputRef.current?.click()}
          style={{
            backgroundColor: tokens.color.orange,
            color: tokens.color.text,
            border: "none",
            borderRadius: tokens.radius.md,
            padding: "6px 12px",
            fontWeight: 500,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: `${tokens.space.md}px`,
            fontSize: "12px",
            transition: "all 0.2s",
            opacity: 1
          }}
          data-testid="ics-upload-btn"
        >
          <Upload style={{ height: "16px", width: "16px" }} />
          Upload .ics
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".ics,.ical"
          style={{ display: "none" }}
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid="ics-file-input"
        />
      </div>
    );
  }

  if (step === "loading") {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: `${tokens.space.md}px`, paddingTop: `${tokens.space.xxl}px`, paddingBottom: `${tokens.space.xxl}px`, justifyContent: "center" }} data-testid="ics-import-loading">
        <Loader2 style={{ height: "20px", width: "20px", animation: "spin 1s linear infinite", color: tokens.color.orange }} />
        <span style={{ fontSize: "12px", color: tokens.color.textMuted }}>Reading calendar file…</span>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (step === "done") {
    return (
      <div style={{ textAlign: "center", paddingTop: `${tokens.space.lg}px`, paddingBottom: `${tokens.space.lg}px` }} data-testid="ics-import-done">
        <Check style={{ height: "32px", width: "32px", color: tokens.color.green, margin: "0 auto 8px auto", display: "block" }} />
        <p style={{ fontSize: "12px", fontWeight: 500, color: tokens.color.text, margin: 0 }}>Import complete!</p>
        <button
          onClick={() => { setStep("idle"); setEvents([]); }}
          style={{
            marginTop: `${tokens.space.md}px`,
            color: tokens.color.textMuted,
            background: "none",
            border: "none",
            cursor: "pointer",
            fontSize: "12px",
            transition: "color 150ms ease"
          }}
          onMouseEnter={(e) => e.currentTarget.style.color = tokens.color.text}
          onMouseLeave={(e) => e.currentTarget.style.color = tokens.color.textMuted}
        >
          Import another file
        </button>
      </div>
    );
  }

  // ── Preview step ──────────────────────────────────────────────────────────
  const allSelected = events.length > 0 && selectedIds.size === events.length;
  const noneSelected = selectedIds.size === 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: `${tokens.space.md}px` }} data-testid="ics-import-preview">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <p style={{ fontSize: "12px", fontWeight: 500, color: tokens.color.text, margin: 0 }}>
          Found {events.length} potential gig{events.length !== 1 ? "s" : ""}
          <span style={{ color: tokens.color.textMuted, fontWeight: 400 }}> from {totalEvents} events</span>
        </p>
        <div style={{ display: "flex", gap: `${tokens.space.md}px` }}>
          <button
            onClick={() => { setStep("idle"); setEvents([]); }}
            style={{
              color: tokens.color.textMuted,
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: "12px",
              transition: "color 150ms ease"
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = tokens.color.text}
            onMouseLeave={(e) => e.currentTarget.style.color = tokens.color.textMuted}
          >
            Back
          </button>
          <button
            onClick={handleImport}
            disabled={importing || noneSelected}
            style={{
              backgroundColor: tokens.color.orange,
              color: tokens.color.text,
              border: "none",
              borderRadius: tokens.radius.md,
              padding: "6px 12px",
              fontWeight: 500,
              cursor: importing || noneSelected ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: `${tokens.space.md}px`,
              fontSize: "12px",
              transition: "all 0.2s",
              opacity: importing || noneSelected ? 0.6 : 1
            }}
            data-testid="ics-import-confirm-btn"
          >
            {importing ? <Loader2 style={{ height: "16px", width: "16px", animation: "spin 1s linear infinite" }} /> : <Check style={{ height: "16px", width: "16px" }} />}
            Import {selectedIds.size > 0 ? selectedIds.size : ""}
          </button>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: `${tokens.space.md}px`, paddingBottom: `${tokens.space.xs}px`, borderBottom: `1px solid ${tokens.color.innerBorder}` }}>
        <button
          onClick={allSelected ? deselectAll : selectAll}
          style={{
            fontSize: "10px",
            color: tokens.color.orange,
            fontWeight: 500,
            background: "none",
            border: "none",
            cursor: "pointer",
            transition: "color 150ms ease"
          }}
          onMouseEnter={(e) => e.currentTarget.style.opacity = "0.8"}
          onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
        >
          {allSelected ? "Deselect all" : "Select all"}
        </button>
        {!noneSelected && !allSelected && (
          <span style={{ fontSize: "10px", color: tokens.color.textMuted }}>{selectedIds.size} selected</span>
        )}
        <button
          onClick={deselectAll}
          style={{
            fontSize: "10px",
            color: tokens.color.textMuted,
            background: "none",
            border: "none",
            cursor: "pointer",
            marginLeft: "auto",
            transition: "color 150ms ease"
          }}
          onMouseEnter={(e) => e.currentTarget.style.color = tokens.color.text}
          onMouseLeave={(e) => e.currentTarget.style.color = tokens.color.textMuted}
        >
          Clear
        </button>
      </div>

      <ScrollArea style={{ height: "420px", paddingRight: `${tokens.space.xs}px` }}>
        <div style={{ display: "flex", flexDirection: "column", gap: `${tokens.space.md}px` }}>
          {events.map((evt, idx) => (
            <div
              key={idx}
              style={{
                padding: `${tokens.space.md}px`,
                borderRadius: tokens.radius.lg,
                border: `1px solid ${selectedIds.has(idx) ? "rgba(245,137,31,0.4)" : tokens.color.innerBorder}`,
                backgroundColor: selectedIds.has(idx) ? "rgba(245,137,31,0.1)" : "rgba(22,22,24,0.5)",
                transition: "all 150ms ease",
                opacity: selectedIds.has(idx) ? 1 : 0.75
              }}
            >
              <div style={{ display: "flex", alignItems: "flex-start", gap: `${tokens.space.md}px` }}>
                <div style={{ marginTop: `${tokens.space.xs}px`, flexShrink: 0 }}>
                  <Checkbox
                    checked={selectedIds.has(idx)}
                    onCheckedChange={() => toggleSelect(idx)}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: `${tokens.space.xs}px` }}>
                  {editIdx === idx ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: `${tokens.space.md}px` }}>
                      <input
                        type="text"
                        value={evt.artist_name}
                        onChange={(e) => updateEvent(idx, "artist_name", e.target.value)}
                        style={{
                          height: "28px",
                          fontSize: "12px",
                          backgroundColor: tokens.color.innerBg,
                          borderColor: tokens.color.innerBorder,
                          color: tokens.color.text,
                          border: `1px solid ${tokens.color.innerBorder}`,
                          borderRadius: tokens.radius.md,
                          padding: `0 ${tokens.space.md}px`,
                          fontFamily: "Geist, sans-serif"
                        }}
                        placeholder="Artist / title"
                      />
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: `${tokens.space.md}px` }}>
                        <input
                          type="text"
                          value={evt.venue_name}
                          onChange={(e) => updateEvent(idx, "venue_name", e.target.value)}
                          style={{
                            height: "28px",
                            fontSize: "12px",
                            backgroundColor: tokens.color.innerBg,
                            borderColor: tokens.color.innerBorder,
                            color: tokens.color.text,
                            border: `1px solid ${tokens.color.innerBorder}`,
                            borderRadius: tokens.radius.md,
                            padding: `0 ${tokens.space.md}px`,
                            fontFamily: "Geist, sans-serif"
                          }}
                          placeholder="Venue"
                        />
                        <input
                          type="text"
                          value={evt.city}
                          onChange={(e) => updateEvent(idx, "city", e.target.value)}
                          style={{
                            height: "28px",
                            fontSize: "12px",
                            backgroundColor: tokens.color.innerBg,
                            borderColor: tokens.color.innerBorder,
                            color: tokens.color.text,
                            border: `1px solid ${tokens.color.innerBorder}`,
                            borderRadius: tokens.radius.md,
                            padding: `0 ${tokens.space.md}px`,
                            fontFamily: "Geist, sans-serif"
                          }}
                          placeholder="City"
                        />
                      </div>
                      <button
                        onClick={() => setEditIdx(null)}
                        style={{
                          fontSize: "10px",
                          height: "24px",
                          color: tokens.color.textMuted,
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          transition: "color 150ms ease"
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.color = tokens.color.text}
                        onMouseLeave={(e) => e.currentTarget.style.color = tokens.color.textMuted}
                      >
                        Done
                      </button>
                    </div>
                  ) : (
                    <>
                      <div style={{ display: "flex", alignItems: "center", gap: `${tokens.space.md}px` }}>
                        <p style={{ fontWeight: 500, fontSize: "12px", truncate: true, flex: 1, color: tokens.color.text, margin: 0 }}>
                          {evt.artist_name || evt.title || "Untitled"}
                        </p>
                        <button
                          onClick={() => setEditIdx(idx)}
                          style={{
                            color: tokens.color.textMuted,
                            flexShrink: 0,
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            transition: "color 150ms ease"
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.color = tokens.color.text}
                          onMouseLeave={(e) => e.currentTarget.style.color = tokens.color.textMuted}
                          title="Edit"
                        >
                          <Edit2 style={{ height: "12px", width: "12px" }} />
                        </button>
                      </div>
                      <p style={{ fontSize: "10px", color: tokens.color.textMuted, margin: 0 }}>
                        {evt.date
                          ? new Date(evt.date + "T12:00:00").toLocaleDateString("en-GB", {
                              weekday: "short", day: "numeric", month: "short", year: "numeric",
                            })
                          : "No date"}
                        {evt.start_time ? ` · ${evt.start_time}` : ""}
                        {evt.end_time ? `–${evt.end_time}` : ""}
                      </p>
                      {evt.location && (
                        <p style={{ fontSize: "10px", color: tokens.color.textMuted, margin: 0 }}>📍 {evt.location}</p>
                      )}
                    </>
                  )}

                  {evt.is_duplicate && (
                    <div style={{
                      fontSize: "10px",
                      color: tokens.color.amber,
                      borderColor: tokens.color.amber,
                      border: "1px solid",
                      backgroundColor: tokens.color.amberSoft,
                      borderRadius: tokens.radius.md,
                      padding: `${tokens.space.xs}px ${tokens.space.md}px`,
                      display: "flex",
                      alignItems: "center",
                      gap: `${tokens.space.xs}px`,
                      width: "fit-content"
                    }}>
                      <AlertTriangle style={{ height: "10px", width: "10px" }} />
                      Possible duplicate
                    </div>
                  )}

                  <div style={{ display: "flex", alignItems: "center", gap: `${tokens.space.md}px`, paddingTop: `${tokens.space.xs}px` }}>
                    <Switch
                      id={`ics-public-${idx}`}
                      checked={evt.is_public}
                      onCheckedChange={(val) => updateEvent(idx, "is_public", val)}
                    />
                    <label htmlFor={`ics-public-${idx}`} style={{ fontSize: "11px", color: tokens.color.textMuted, cursor: "pointer", margin: 0 }}>
                      {evt.is_public ? "Public" : "Private"}
                    </label>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};
